import { createContext, useContext, useEffect, useState } from "react";
import { getSiteStrings } from "@/services/siteStringsService";
import { TEXT_REGISTRY } from "./textRegistry";

const SiteTextContext = createContext(null); // null = not loaded yet

/** Fetches the published site-strings map once per app load (not once per
 * string — hundreds of components call useSiteText, so a per-component
 * fetch would mean hundreds of redundant reads of the same doc) and hands
 * it down via context. Mount once, high in the tree (see App.js). */
export function SiteTextProvider({ children }) {
  const [published, setPublished] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSiteStrings();
        if (!cancelled) setPublished(data?.published || {});
      } catch (err) {
        console.error("Failed to load site text:", err);
        // Falls through to per-key defaultValue below rather than a blank UI.
        if (!cancelled) setPublished({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SiteTextContext.Provider value={published}>
      {children}
    </SiteTextContext.Provider>
  );
}

/** Reads one CMS-editable string by its textRegistry.js key. The
 * registry's defaultValue is the single source of truth for what ships
 * before an admin edits anything (and for what shows if Firestore has
 * nothing for this key yet) — callers never pass their own fallback, so
 * there's no way for a call site's fallback text to drift from what the
 * admin actually sees labeled in the editor. */
export function useSiteText(key) {
  const published = useContext(SiteTextContext);
  const def = TEXT_REGISTRY[key];

  if (!def && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`useSiteText: "${key}" isn't registered in textRegistry.js`);
  }
  const fallback = def ? def.defaultValue : key;

  if (!published) return fallback; // still loading
  const value = published[key];
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

/** Bulk variant for components that need many strings at once (a form
 * with a dozen field labels, say) — one shared context read instead of
 * one useSiteText() call per string. `keys` maps a local name to a
 * textRegistry.js key:
 *   const t = useSiteTexts({ title: "auth.profile.title", ... });
 *   t.title // resolved string
 */
export function useSiteTexts(keys) {
  const published = useContext(SiteTextContext);
  const result = {};
  for (const [localName, key] of Object.entries(keys)) {
    const def = TEXT_REGISTRY[key];
    if (!def && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`useSiteTexts: "${key}" isn't registered in textRegistry.js`);
    }
    const fallback = def ? def.defaultValue : key;
    const value = published?.[key];
    result[localName] =
      typeof value === "string" && value.trim() !== "" ? value : fallback;
  }
  return result;
}
